/*
 * Maison Alia — KIDS domain module (TACKids)
 * ------------------------------------------
 * Kids-owned business + STATE logic only: mattress matrix, curated 4-piece
 * composition, recommendations, personalization, optional Flat/Square, and the
 * full configurator state machine (multi-line shams, quantities, merge,
 * non-destructive recommendation override + Apply, remove/re-add memory,
 * availability gating). All bundle/cart infrastructure is delegated to
 * TACBundleCore. This module never duplicates cart/bundle plumbing and never
 * reaches into the Adult configurator.
 *
 * Pure & framework-free: the reducer returns new state; selectors are pure.
 * UMD: browser global `TACKids` (uses window.TACBundleCore) + CommonJS
 * (core injected) for the offline harness.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = function (core) { return factory(core); };
  else root.TACKids = factory(root.TACBundleCore);
}(typeof self !== 'undefined' ? self : this, function (Core) {
  'use strict';

  var TIER_LABEL = { standard: 'Standard', light: 'Light', heavy: 'Heavy' };
  var FABRIC_LABEL = { cotton: 'ALIA Cotton Sateen', signature: 'ALIA Signature' };
  var DEFAULT_DEPTH = 'Up to 10 cm (4 in)';
  var DEPTHS = ['Up to 10 cm (4 in)', 'Up to 15 cm (6 in)', 'Up to 20 cm (8 in)', 'Up to 25 cm (10 in)', 'Up to 30 cm (12 in)'];
  var SHAM_SIZES = ['Kid — 30 × 40 cm', 'Junior — 40 × 60 cm', 'Standard / American — 45 × 70 cm'];
  var SQUARE_SIZES = ['Kids Square — 40 × 40 cm', 'French Square — 60 × 60 cm'];

  // Mattress matrix. Extended lengths reuse the sibling duvet/sham family
  // (identical price, longer fitted only) — approved mappings, not inferred.
  var MATTRESS = {
    'infant-60x120':  { label: 'Infant · 60 × 120 cm',  fitted: '60 × 120 cm', duvet: '100 × 135 cm', sham: 'Kid — 30 × 40 cm',                flat: '120 × 180 cm' },
    'toddler-70x120': { label: 'Toddler · 70 × 120 cm', fitted: '70 × 120 cm', duvet: '120 × 150 cm', sham: 'Kid — 30 × 40 cm',                flat: '120 × 180 cm' },
    'toddler-70x140': { label: 'Toddler · 70 × 140 cm', fitted: '70 × 140 cm', duvet: '120 × 150 cm', sham: 'Kid — 30 × 40 cm',                flat: '130 × 200 cm' },
    'junior-80x190':  { label: 'Junior · 80 × 190 cm',  fitted: '80 × 190 cm', duvet: '130 × 200 cm', sham: 'Junior — 40 × 60 cm',             flat: '170 × 290 cm' },
    'junior-80x200':  { label: 'Junior · 80 × 200 cm',  fitted: '80 × 200 cm', duvet: '130 × 200 cm', sham: 'Junior — 40 × 60 cm',             flat: '170 × 290 cm' },
    'teen-90x190':    { label: 'Teen · 90 × 190 cm',    fitted: '90 × 190 cm', duvet: '140 × 200 cm', sham: 'Standard / American — 45 × 70 cm', flat: '180 × 290 cm' },
    'teen-90x200':    { label: 'Teen · 90 × 200 cm',    fitted: '90 × 200 cm', duvet: '140 × 200 cm', sham: 'Standard / American — 45 × 70 cm', flat: '180 × 290 cm' }
  };

  // ---- Personalization (Kids-owned rules) ---------------------------------
  var PERSO_ALLOWED = /^[\p{L} .'’ʼ-]+$/u;   // letters+accents, space, period, ' ’ ʼ, hyphen
  var PERSO_MAX = 16;
  function validatePersonalization(raw) {
    if (raw == null) return { ok: true, omit: true, text: '' };
    var t = Core.normalizeText(raw);                                 // NFC
    if (/[\x00-\x1F\x7F]/.test(t)) return { ok: false, reason: 'control', text: t };
    t = t.trim();
    if (t === '') return { ok: true, omit: true, text: '' };
    if (/\p{N}/u.test(t)) return { ok: false, reason: 'no-digits', text: t };
    if (!PERSO_ALLOWED.test(t)) return { ok: false, reason: 'invalid-char', text: t };
    if (Array.from(t).length > PERSO_MAX) return { ok: false, reason: 'too-long', text: t };
    return { ok: true, omit: false, text: t };
  }

  function recommendedShamSize(mattress) { return MATTRESS[mattress].sham; }
  function recommendedFlatSize(mattress) { return MATTRESS[mattress] ? MATTRESS[mattress].flat : null; }
  function recommendedDuvetSize(mattress) { return MATTRESS[mattress] ? MATTRESS[mattress].duvet : null; }

  // ---- helpers ------------------------------------------------------------
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function mergeLines(list) {          // combine same-size lines, keep first order
    var out = [], idx = {};
    list.forEach(function (l) {
      var q = Math.max(0, l.qty | 0); if (q <= 0) return;
      if (idx[l.size] != null) out[idx[l.size]].qty += q;
      else { idx[l.size] = out.length; out.push({ size: l.size, qty: q }); }
    });
    return out;
  }

  // ---- State machine ------------------------------------------------------
  function initState(o) {
    o = o || {};
    var mattress = o.mattress || 'infant-60x120';
    return {
      mattress: mattress, fabric: o.fabric || 'cotton', tier: (o.tier || 'standard'),
      depth: o.depth || DEFAULT_DEPTH, personalization: o.personalization || '',
      sleeping: { lines: [{ size: recommendedShamSize(mattress), qty: 1 }], overridden: false, memory: null },
      duvet: { size: recommendedDuvetSize(mattress), overridden: false },
      flat: { active: false, size: null, overridden: false, memory: null },
      square: { lines: [], memory: null }
    };
  }

  function reduce(state, a) {
    var s = clone(state);
    switch (a.type) {
      case 'mattress':
        s.mattress = a.mattress;
        if (!s.sleeping.overridden)
          s.sleeping.lines = [{ size: recommendedShamSize(s.mattress), qty: (s.sleeping.lines[0] ? s.sleeping.lines[0].qty : 1) }];
        if (s.flat.active && !s.flat.overridden) s.flat.size = recommendedFlatSize(s.mattress);
        if (!s.duvet.overridden) s.duvet.size = recommendedDuvetSize(s.mattress);
        return s;
      case 'fabric': s.fabric = a.fabric; return s;
      case 'depth': s.depth = a.depth; return s;
      case 'personalization': s.personalization = a.value; return s;

      case 'sleeping.size':
        if (s.sleeping.lines[a.index]) { s.sleeping.lines[a.index].size = a.size; s.sleeping.overridden = true; s.sleeping.lines = mergeLines(s.sleeping.lines); }
        return s;
      case 'sleeping.qty':
        if (s.sleeping.lines[a.index]) { s.sleeping.lines[a.index].qty = Math.max(1, a.qty | 0); s.sleeping.overridden = true; }
        return s;
      case 'sleeping.add': {
        var addSize = a.size || firstUnused(s.sleeping.lines, SHAM_SIZES);
        if (!addSize) return s;
        s.sleeping.overridden = true;
        s.sleeping.lines = mergeLines(s.sleeping.lines.concat([{ size: addSize, qty: 1 }]));
        return s;
      }
      case 'sleeping.remove':
        if (s.sleeping.lines[a.index]) { s.sleeping.memory = clone(s.sleeping.lines[a.index]); s.sleeping.lines.splice(a.index, 1); s.sleeping.overridden = true; }
        return s;
      case 'sleeping.readd':
        if (s.sleeping.memory) { s.sleeping.lines = mergeLines(s.sleeping.lines.concat([clone(s.sleeping.memory)])); s.sleeping.memory = null; s.sleeping.overridden = true; }
        return s;
      case 'sleeping.apply': {
        var recSize = recommendedShamSize(s.mattress);
        if (!s.sleeping.lines.length) s.sleeping.lines = [{ size: recSize, qty: 1 }];
        else { s.sleeping.lines[0].size = recSize; s.sleeping.lines = mergeLines(s.sleeping.lines); }
        if (s.sleeping.lines.length === 1 && s.sleeping.lines[0].size === recSize && s.sleeping.lines[0].qty === 1) s.sleeping.overridden = false;
        return s;
      }

      case 'flat.enable':
        s.flat.active = true;
        if (s.flat.memory) { s.flat.size = s.flat.memory.size; s.flat.overridden = s.flat.memory.overridden; s.flat.memory = null; }
        else { s.flat.size = recommendedFlatSize(s.mattress); s.flat.overridden = false; }
        return s;
      case 'flat.size': s.flat.active = true; s.flat.size = a.size; s.flat.overridden = true; return s;
      case 'flat.remove': if (s.flat.active) { s.flat.memory = { size: s.flat.size, overridden: s.flat.overridden }; s.flat.active = false; } return s;
      case 'flat.apply': s.flat.size = recommendedFlatSize(s.mattress); s.flat.overridden = false; return s;

      case 'duvet.size': s.duvet.size = a.size; s.duvet.overridden = true; return s;
      case 'duvet.apply': s.duvet.size = recommendedDuvetSize(s.mattress); s.duvet.overridden = false; return s;

      case 'square.add': {
        var sqSize = a.size || firstUnused(s.square.lines, SQUARE_SIZES);
        if (!sqSize) return s;
        s.square.lines = mergeLines(s.square.lines.concat([{ size: sqSize, qty: 1 }]));
        return s;
      }
      case 'square.size':
        if (s.square.lines[a.index] && SQUARE_SIZES.indexOf(a.size) >= 0) { s.square.lines[a.index].size = a.size; s.square.lines = mergeLines(s.square.lines); }
        return s;
      case 'square.qty': if (s.square.lines[a.index]) s.square.lines[a.index].qty = Math.max(1, a.qty | 0); return s;
      case 'square.remove': if (s.square.lines[a.index]) { s.square.memory = clone(s.square.lines[a.index]); s.square.lines.splice(a.index, 1); } return s;
      case 'square.readd': if (s.square.memory) { s.square.lines = mergeLines(s.square.lines.concat([clone(s.square.memory)])); s.square.memory = null; } return s;

      default: return s;
    }
  }

  function firstUnused(lines, all) {
    var used = {}; lines.forEach(function (l) { used[l.size] = true; });
    for (var i = 0; i < all.length; i++) if (!used[all[i]]) return all[i];
    return null;
  }

  // ---- Selectors ----------------------------------------------------------
  function lines(state) {
    var m = MATTRESS[state.mattress], out = [];
    out.push({ role: 'fitted', component: 'fitted', componentType: 'Fitted Sheet', size: m.fitted, tiered: false, depth: state.depth, qty: 1 });
    out.push({ role: 'duvet', component: 'duvet', componentType: 'Duvet Cover', size: state.duvet.size, tiered: true, qty: 1 });
    state.sleeping.lines.forEach(function (l) { out.push({ role: 'sleeping_sham', component: 'rect', componentType: 'Sleeping Pillow Sham', size: l.size, tiered: true, qty: l.qty }); });
    out.push({ role: 'personalized_sham', component: 'perso', componentType: 'Personalized Pillow Sham', size: m.sham, tiered: true, qty: 1 });
    if (state.flat.active && state.flat.size) out.push({ role: 'flat', component: 'flat', componentType: 'Flat Sheet', size: state.flat.size, tiered: true, qty: 1 });
    state.square.lines.forEach(function (l) { out.push({ role: 'square_sham', component: 'square', componentType: 'Square Pillow Sham', size: l.size, tiered: true, qty: l.qty }); });
    return out;
  }

  function pieceCount(state) { return lines(state).reduce(function (n, it) { return n + it.qty; }, 0); }

  function priceOf(catalog, component, fabric, size, key) {
    var c = catalog[component] && catalog[component][fabric] && catalog[component][fabric][size];
    if (!c || !(key in c)) throw new Error('no price for ' + component + '/' + fabric + '/' + size + '/' + key);
    return c[key];
  }
  function lineKey(it, tier) { return it.tiered ? tier : it.depth; }
  function totalPrice(state, priceCatalog) {
    return lines(state).reduce(function (s, it) { return s + priceOf(priceCatalog, it.component, state.fabric, it.size, lineKey(it, state.tier)) * it.qty; }, 0);
  }

  function recommendations(state) {
    var m = MATTRESS[state.mattress];
    var sl = state.sleeping.lines;
    return {
      sleeping: { size: m.sham, show: sl.length ? sl[0].size !== m.sham : true },
      duvet: { size: m.duvet, show: state.duvet.size !== m.duvet },
      flat: { size: m.flat, show: state.flat.active && state.flat.overridden && state.flat.size !== m.flat }
    };
  }

  function sleepingCanAddMore(state) { return state.sleeping.lines.length < SHAM_SIZES.length; }
  function squareCanAddMore(state) { return state.square.lines.length < SQUARE_SIZES.length; }
  function availableSizes(lineList, all) { var used = {}; lineList.forEach(function (l) { used[l.size] = true; }); return all.filter(function (x) { return !used[x]; }); }

  function canAddToCart(state, byId) {
    var perso = validatePersonalization(state.personalization);
    if (!perso.ok) return { ok: false, reason: 'personalization', detail: perso.reason };
    var problems = [];
    lines(state).forEach(function (it) {
      var opt3 = it.tiered ? TIER_LABEL[state.tier] : it.depth;
      var v = Core.findVariant(byId[it.component], { fabric: FABRIC_LABEL[state.fabric], size: it.size, opt3: opt3 });
      if (!v) problems.push({ kind: 'missing', line: it.componentType, size: it.size });
      else if (v.available !== true) problems.push({ kind: 'unavailable', line: it.componentType, size: it.size });
    });
    return problems.length ? { ok: false, reason: 'variants', problems: problems } : { ok: true };
  }

  function cartItems(state, byId, ctx) {
    var gate = canAddToCart(state, byId);
    if (!gate.ok) throw new Error('cartItems blocked by gate: ' + gate.reason + (gate.detail ? '/' + gate.detail : ''));
    var bundleId = (ctx && ctx.bundleId) || Core.newBundleId('kids');
    var tierLabel = TIER_LABEL[state.tier];
    var perso = validatePersonalization(state.personalization);
    return lines(state).map(function (it) {
      var opt3 = it.tiered ? tierLabel : it.depth;
      var v = Core.findVariant(byId[it.component], { fabric: ctx.fabricLabel, size: it.size, opt3: opt3 });
      if (!v || v.available !== true) throw new Error('unusable variant ' + it.component + '/' + it.size + '/' + opt3);
      var personalization = (it.role === 'personalized_sham' && perso.ok && !perso.omit) ? perso.text : null;
      return {
        id: Number(v.id),
        quantity: it.qty,
        properties: Core.buildProperties({
          set: ctx.set, collection: ctx.collection, embroidery: ctx.embroidery,
          fabric: ctx.fabricLabel, size: it.size,
          depth: it.tiered ? null : it.depth,
          tier: it.tiered ? tierLabel : null,
          personalization: personalization,
          bundleId: bundleId, bundleHandle: ctx.bundleHandle,
          componentType: it.componentType, line: 'kids'
        })
      };
    });
  }

  function buildComposition(opts) { return lines(initState(opts)); }
  function total(opts, priceCatalog) { return totalPrice(initState(opts), priceCatalog); }

  return {
    TIER_LABEL: TIER_LABEL, FABRIC_LABEL: FABRIC_LABEL, DEFAULT_DEPTH: DEFAULT_DEPTH,
    DEPTHS: DEPTHS, SHAM_SIZES: SHAM_SIZES, SQUARE_SIZES: SQUARE_SIZES, MATTRESS: MATTRESS,
    validatePersonalization: validatePersonalization,
    recommendedShamSize: recommendedShamSize, recommendedFlatSize: recommendedFlatSize, recommendedDuvetSize: recommendedDuvetSize,
    initState: initState, reduce: reduce,
    lines: lines, pieceCount: pieceCount, totalPrice: totalPrice, priceOf: priceOf,
    recommendations: recommendations,
    sleepingCanAddMore: sleepingCanAddMore, squareCanAddMore: squareCanAddMore, availableSizes: availableSizes,
    canAddToCart: canAddToCart, cartItems: cartItems,
    buildComposition: buildComposition, total: total
  };
}))
